import { useEffect, useState, useCallback } from 'react';

const STORAGE_KEYS = {
  token: 'crystal_guest_token',
  user: 'crystal_guest_user',     // { user_id, email, name }
  faves: 'crystal_guest_faves',   // local cache: [{ item_type, item_id }]
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

const readJSON = (key, fallback) => {
  try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fallback; } catch { return fallback; }
};
const writeJSON = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* ignore */ }
};

let inMemoryListeners = [];
const broadcast = () => inMemoryListeners.forEach(fn => { try { fn(); } catch { /* ignore */ } });

/**
 * Guest identity hook — localStorage-first.
 *
 * - Tracks favourites locally instantly. No login required to heart things.
 * - When the visitor wants to share / create an inspo list, call `register(email, name)`
 *   which mints a guest JWT, persists it, and replays any local favourites to the server.
 */
export function useGuestIdentity() {
  const [token, setTokenState] = useState(() => localStorage.getItem(STORAGE_KEYS.token) || '');
  const [user, setUserState] = useState(() => readJSON(STORAGE_KEYS.user, null));
  const [faves, setFavesState] = useState(() => readJSON(STORAGE_KEYS.faves, []));

  useEffect(() => {
    const fn = () => {
      setTokenState(localStorage.getItem(STORAGE_KEYS.token) || '');
      setUserState(readJSON(STORAGE_KEYS.user, null));
      setFavesState(readJSON(STORAGE_KEYS.faves, []));
    };
    inMemoryListeners.push(fn);
    return () => { inMemoryListeners = inMemoryListeners.filter(f => f !== fn); };
  }, []);

  const isFav = useCallback((item_type, item_id) =>
    faves.some(f => f.item_type === item_type && f.item_id === item_id),
  [faves]);

  const _save = (next) => {
    writeJSON(STORAGE_KEYS.faves, next);
    setFavesState(next);
    broadcast();
  };

  const toggleFav = useCallback(async (item_type, item_id) => {
    const exists = faves.some(f => f.item_type === item_type && f.item_id === item_id);
    if (exists) {
      const next = faves.filter(f => !(f.item_type === item_type && f.item_id === item_id));
      _save(next);
      // Best-effort server delete (only if already registered)
      const t = localStorage.getItem(STORAGE_KEYS.token);
      if (t) {
        try {
          await fetch(`${API_URL}/api/favourites?item_type=${encodeURIComponent(item_type)}&item_id=${encodeURIComponent(item_id)}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${t}` },
          });
        } catch (e) { console.error('useGuestIdentity: server fave delete failed (kept local)', e); }
      }
      return false;
    } else {
      const entry = { item_type, item_id, added_at: new Date().toISOString() };
      _save([entry, ...faves]);
      const t = localStorage.getItem(STORAGE_KEYS.token);
      if (t) {
        try {
          await fetch(`${API_URL}/api/favourites`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${t}` },
            body: JSON.stringify({ item_type, item_id }),
          });
        } catch (e) { console.error('useGuestIdentity: server fave add failed (kept local)', e); }
      }
      return true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faves]);

  /** Register / sign-in email+name. Replays local favourites to server. */
  const register = useCallback(async (email, name) => {
    // Allow email-only registration — fall back to email-local-part as display name
    const displayName = (name && name.trim()) || (String(email || '').split('@')[0] || 'Guest');
    const res = await fetch(`${API_URL}/api/inspo/register-guest`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, name: displayName }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Could not register');
    }
    const data = await res.json();
    localStorage.setItem(STORAGE_KEYS.token, data.token);
    writeJSON(STORAGE_KEYS.user, { user_id: data.user_id, email: data.email, name: data.name });
    setTokenState(data.token);
    setUserState({ user_id: data.user_id, email: data.email, name: data.name });
    broadcast();

    // Replay any local favourites that aren't yet on the server
    try {
      const current = readJSON(STORAGE_KEYS.faves, []);
      await Promise.all(current.map(f =>
        fetch(`${API_URL}/api/favourites`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${data.token}` },
          body: JSON.stringify({ item_type: f.item_type, item_id: f.item_id }),
        }).catch((e) => console.error('useGuestIdentity: replay fave failed for', f, e))
      ));
    } catch (e) { console.error('useGuestIdentity: fave replay batch failed', e); }
    return data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEYS.token);
    localStorage.removeItem(STORAGE_KEYS.user);
    setTokenState('');
    setUserState(null);
    broadcast();
  }, []);

  return {
    token,
    user,
    faves,
    isFav,
    toggleFav,
    register,
    logout,
    isRegistered: !!token && !!user,
  };
}
